import { getJourneyChipLabel, getJourneyProgress } from './journeyDefinition';
import { isCompletedSourceStatus } from './collaborationDisplayStatus';

function isCancelledAcceptance(acceptanceDetail) {
  const acceptanceStatus = acceptanceDetail?.acceptanceStatus?.toLowerCase?.()
    || acceptanceDetail?.status?.toLowerCase?.();
  return acceptanceStatus === 'cancelled' || acceptanceStatus === 'canceled';
}

function buildRowStepSource(item, acceptanceDetail) {
  if (acceptanceDetail) {
    const mergedStatus = acceptanceDetail.status || item.status;
    const status = (
      isCompletedSourceStatus(item?.status)
      || isCompletedSourceStatus(mergedStatus)
      || isCompletedSourceStatus(acceptanceDetail?.acceptanceStatus)
    ) ? 'completed' : mergedStatus;
    return {
      ...item,
      ...acceptanceDetail,
      status,
    };
  }
  if (isCompletedSourceStatus(item?.status)) {
    return { ...item, status: 'completed' };
  }
  if (item.status === 'approved') {
    return { ...item, status: 'accepted' };
  }
  return item;
}

/**
 * Current journey step for a collaboration list/table row.
 * @returns {{ label: string, isWaiting: boolean, currentStep: number, totalSteps: number } | null}
 */
export function resolveRowJourneyStep({ item, acceptanceDetail, role, isAccepted }) {
  if (!isAccepted || !item) return null;
  if (acceptanceDetail && isCancelledAcceptance(acceptanceDetail)) return null;

  const stepSource = buildRowStepSource(item, acceptanceDetail);
  const chipMeta = getJourneyChipLabel(stepSource, role);
  const progress = getJourneyProgress(stepSource);
  const { totalSteps } = progress;
  const isComplete = stepSource.status === 'completed' || chipMeta.status === 'complete';
  const completedSteps = isComplete
    ? totalSteps
    : Math.min(progress.completedCount, totalSteps);
  const progressPercent = totalSteps > 0
    ? Math.max(0, Math.min(100, Math.round((completedSteps / totalSteps) * 100)))
    : 0;

  return {
    label: chipMeta.label,
    isWaiting: chipMeta.isWaiting,
    status: chipMeta.status,
    statusLabel: chipMeta.statusLabel,
    ownerRole: chipMeta.ownerRole,
    ownerLabel: chipMeta.ownerLabel,
    primaryCtaLabel: chipMeta.primaryCtaLabel,
    blockedReason: chipMeta.blockedReason,
    currentStep: isComplete ? totalSteps : progress.currentStep,
    totalSteps,
    progressPercent,
  };
}

export function buildJourneyListItem({
  item,
  detailKey,
  isApplication,
  stepSource,
  stepMeta,
  acceptanceDetail,
  resolveJourneyProgress = getJourneyProgress,
  role = 'brand',
}) {
  const acceptanceId = acceptanceDetail?.acceptanceId
    ?? acceptanceDetail?.acceptance_id
    ?? acceptanceDetail?.id
    ?? item.acceptance_id
    ?? item.acceptanceId
    ?? null;
  const progress = resolveJourneyProgress(stepSource, role);
  const { totalSteps } = progress;
  const completedSteps = Math.min(progress.completedCount, totalSteps);
  const progressPercent = Math.max(0, Math.min(100, Math.round((completedSteps / totalSteps) * 100)));

  return {
    ...item,
    source_item_id: item.id,
    id: detailKey,
    journey_type: isApplication ? 'Application' : 'Invite',
    journey_label: stepMeta.label,
    journey_waiting: stepMeta.isWaiting,
    journey_status: stepMeta.status,
    journey_status_label: stepMeta.statusLabel,
    journey_owner_role: stepMeta.ownerRole,
    journey_owner_label: stepMeta.ownerLabel,
    journey_primary_cta: stepMeta.primaryCtaLabel,
    journey_blocked_reason: stepMeta.blockedReason,
    completed_steps: completedSteps,
    total_steps: totalSteps,
    progress_percent: progressPercent,
    journey_acceptance_id: acceptanceId,
    created_at: item.created_at,
  };
}

/** True when every journey step is done (including the final review). */
export function isJourneyItemComplete(item) {
  if (!item) return false;
  if (item.journey_label === 'Complete') return true;
  const total = Number(item.total_steps);
  const completed = Number(item.completed_steps);
  return total > 0 && completed >= total;
}

/**
 * Split journey items the same way Collaborations Active / Completed tabs do.
 * @param {object[]} [journeyItems]
 * @param {(item: object) => boolean} [isCompletedExtra] optional completed display-status check
 */
export function partitionActiveAndCompletedJourneyItems(
  journeyItems = [],
  isCompletedExtra = () => false,
) {
  const activeJourneyItems = [];
  const completedJourneyItems = [];
  journeyItems.forEach((item) => {
    if (isJourneyItemComplete(item) || isCompletedExtra(item)) {
      completedJourneyItems.push(item);
    } else {
      activeJourneyItems.push(item);
    }
  });
  return { activeJourneyItems, completedJourneyItems };
}

export function countActiveJourneyItems(journeyItems = [], isCompletedExtra = () => false) {
  return partitionActiveAndCompletedJourneyItems(journeyItems, isCompletedExtra)
    .activeJourneyItems.length;
}

export function buildJourneyStepStats(journeyItems, waitingKey = 'waiting') {
  const byStep = journeyItems.reduce((acc, item) => {
    const key = item.journey_label || 'Next step';
    acc[key] = (acc[key] || 0) + 1;
    return acc;
  }, {});

  const topStep = Object.entries(byStep)
    .sort((a, b) => b[1] - a[1])[0];

  return {
    total: journeyItems.length,
    [waitingKey]: journeyItems.filter((item) => item.journey_waiting).length,
    actionNeeded: journeyItems.filter(
      (item) => !item.journey_waiting && !isJourneyItemComplete(item),
    ).length,
    completed: journeyItems.filter((item) => isJourneyItemComplete(item)).length,
    topStepLabel: topStep?.[0] || '—',
    topStepCount: topStep?.[1] || 0,
  };
}

export function partitionJourneyItems(journeyItems) {
  const actionNeededItems = [];
  const waitingItems = [];
  const completedItems = [];

  journeyItems.forEach((row) => {
    if (isJourneyItemComplete(row)) {
      completedItems.push(row);
    } else if (row.journey_waiting) {
      waitingItems.push(row);
    } else {
      actionNeededItems.push(row);
    }
  });

  const byUrgency = (a, b) => {
    if (a.progress_percent !== b.progress_percent) return a.progress_percent - b.progress_percent;
    return new Date(a.created_at) - new Date(b.created_at);
  };

  actionNeededItems.sort(byUrgency);
  waitingItems.sort(byUrgency);
  completedItems.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

  return { actionNeededItems, waitingItems, completedItems };
}
